import StanDivider from '@/components/core-components/StanDivider/StanDivider';
import { StanButton, StanInput } from '@/components';
import IconEmail from '@/assets/icons/ic-mail.svg';
import Logo from '@/assets/images/stan-logo-text.png';
import { useStanRouter } from '@/hooks/useStanRouter';
import { forgotPasswordSchema } from '@/features/auth/validations';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ForgotPasswordFormState } from '@/features/auth/types';
import { useForgotPasswordMutation } from '@/features/auth/queries';
import { stanNotify } from '@/lib';
import { useState } from 'react';

export default function ForgotPasswordPage() {
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<ForgotPasswordFormState>({
        resolver: zodResolver(forgotPasswordSchema),
        defaultValues: {
            email: '',
        },
    });

    const { push } = useStanRouter();
    const { mutate: forgotPasswordMutate, isPending: isLoading } = useForgotPasswordMutation();
    const [emailSent, setEmailSent] = useState<boolean>(false);
    const email = watch('email');

    const sendForgotPasswordEmail = async (data: ForgotPasswordFormState) => {
        forgotPasswordMutate(data, {
            onSuccess: () => {
                setEmailSent(true);
                stanNotify({
                    type: 'success',
                    title: 'Forgot Password',
                    text: 'An email has been sent to your email address.',
                    duration: 5000,
                });
            },
            onError: error => {
                stanNotify({
                    type: 'error',
                    title: 'Oops.',
                    text: 'Something went wrong. Please try again later.',
                    duration: 2000,
                    error: {
                        message:
                            error instanceof Error ? error.message : 'Something went wrong. Please try again later.',
                        options: {
                            context: {
                                email: data.email,
                            },
                        },
                    },
                });
            },
        });
    };

    if (emailSent) {
        return (
            <div className='w-full max-w-md space-y-8 p-8'>
                <div className='flex flex-col items-center gap-2'>
                    <img className='mx-auto mb-6' src={Logo} height={100} width={100} alt='Stan Logo' />
                    <p className='text-heading-heading2 text-center'>Forgot Your Password?</p>
                    <p className='text-body-2-regular text-center'>
                        If an account under {email}, you should receive an email with password reset instructions
                        shortly!
                    </p>
                </div>

                <div className='mt-8 space-y-6'>
                    <form className='space-y-4' onSubmit={handleSubmit(sendForgotPasswordEmail)}>
                        <div className='mb-6 flex flex-col items-center justify-between gap-2'>
                            <div className='text-body-2-bold text-dark'>
                                Didn&apos;t receive an email?{' '}
                                <span className='link-text' onClick={handleSubmit(sendForgotPasswordEmail)}>
                                    Send again
                                </span>
                            </div>
                        </div>
                        <div className='flex flex-col items-center justify-between gap-2'>
                            <StanDivider label='or' />
                        </div>
                        <div className='mb-6 flex flex-col items-center justify-between gap-2'>
                            <div className='text-body-2-bold text-dark'>
                                Go back to{' '}
                                <span
                                    className='link-text'
                                    onClick={e => {
                                        e.preventDefault();
                                        push('/login');
                                    }}
                                >
                                    Login
                                </span>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        );
    }
    return (
        <div className='w-full max-w-md space-y-8 p-8'>
            <div className='flex flex-col items-center gap-2'>
                <img className='mx-auto mb-6' src={Logo} height={100} width={100} alt='Stan Logo' />
                <p className='text-heading-heading2 text-center'>Forgot Your Password?</p>
                <p className='text-body-1-regular text-center'>
                    We do it all the time too - we&apos;ll send your email a reset link!
                </p>
            </div>

            <div className='mt-8 space-y-6'>
                <form className='space-y-4' onSubmit={handleSubmit(sendForgotPasswordEmail)}>
                    <StanInput
                        id='email'
                        type='email'
                        placeholder='Email'
                        disabled={isLoading}
                        hintText={errors.email?.message}
                        error={!!errors.email}
                        startIcon={IconEmail}
                        value={email}
                        {...register('email')}
                    />

                    <div className='mb-12 flex flex-col items-center justify-between gap-2'>
                        <StanButton
                            type='submit'
                            color='primary'
                            loading={isLoading}
                            disabled={isLoading}
                            className='w-full'
                            name='sign-in'
                        >
                            Submit
                        </StanButton>

                        <div className='text-body-2-bold text-dark'>
                            Go back to{' '}
                            <span
                                className='link-text'
                                onClick={e => {
                                    e.preventDefault();
                                    push('/login');
                                }}
                            >
                                Login
                            </span>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
